import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import PageHeader from './PageHeader';
import DocumentCollaborationStatus from './DocumentCollaborationStatus';
import { DownloadOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, FileExcelOutlined, FilePptOutlined, EditOutlined, HistoryOutlined } from '@ant-design/icons';
import { Button, Modal, Space, Alert, Typography, Steps, message } from 'antd';
import '../styles/document-viewer.css';
import signedUrlService from '../services/signedUrlService';
import { getDocumentStoragePath, constructOrgDocumentPath } from '../utils/storagePathExtractor';
import { 
  createEditSession, 
  generateSmartDownloadUrl, 
  updateEditSession,
  completeEditSession 
} from '../services/documentEditService';

const { Text } = Typography;

const DocumentViewer = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signedUrl, setSignedUrl] = useState(null);
  const [proxyUrl, setProxyUrl] = useState(null);
  const [signedUrlLoading, setSignedUrlLoading] = useState(false);
  const [viewerError, setViewerError] = useState(false);
  const [currentViewer, setCurrentViewer] = useState('office'); // 'office', 'google', 'download'
  
  // Enhanced edit workflow state
  const [editSession, setEditSession] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentEditStep, setCurrentEditStep] = useState(0);
  const [downloadInfo, setDownloadInfo] = useState(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!documentId) {
          throw new Error('Document ID not provided');
        }

        if (!currentUser?.orgId) {
          throw new Error('User organization not found');
        }

        // Fetch document from Firestore
        const docRef = doc(db, 'c3_documents', documentId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          throw new Error('Document not found');
        }

        const docData = docSnap.data();

        // Check if user has permission to view this document
        if (docData.orgId !== currentUser.orgId && !currentUser.isAdmin) {
          throw new Error('You do not have permission to view this document');
        }

        const documentData = { id: docSnap.id, ...docData };
        setDocumentData(documentData);
        
        // Generate appropriate URL based on file type
        if (isOfficeFile(documentData.type)) {
          // Use proxy for Office files (better reliability)
          try {
            await generateProxyUrlForViewing(documentData);
          } catch (proxyError) {
            console.log('🔄 Proxy URL generation failed, falling back to signed URL for Office file:', proxyError.message);
            await generateSignedUrlForViewing(documentData);
          }
        } else {
          // Use signed URL for PDFs and other files
          await generateSignedUrlForViewing(documentData);
        }
        
      } catch (err) {
        console.error('Error fetching document:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [documentId, currentUser, db]);

  const generateSignedUrlForViewing = async (documentData) => {
    try {
      setSignedUrlLoading(true);
      console.log('🔐 Generating signed URL for viewing:', documentData.name);
      
      // Get storage path using utility
      const fallbackFileName = documentData.versions?.[documentData.versions.length - 1]?.fileName || documentData.name;
      const fallbackPath = constructOrgDocumentPath(currentUser.orgId, fallbackFileName);
      const storagePath = getDocumentStoragePath(documentData, fallbackPath);

      // Generate signed URL for viewing (15 minutes expiry)
      const viewingSignedUrl = await signedUrlService.generateSignedUrl(storagePath, 15);
      setSignedUrl(viewingSignedUrl);
      
      console.log('✅ Signed URL for viewing generated successfully');
      
    } catch (error) {
      console.error('❌ Error generating signed URL for viewing:', error);
      // Fallback: use original URL
      setSignedUrl(documentData.url);
    } finally {
      setSignedUrlLoading(false);
    }
  };

  const generateProxyUrlForViewing = async (documentData) => {
    try {
      setSignedUrlLoading(true); // Reuse same loading state
      console.log('🔐 Generating proxy URL for Office document viewing:', documentData.name);
      
      // Get Firebase ID token from the auth object directly
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('User not authenticated');
      }
      
      const idToken = await firebaseUser.getIdToken();
      
      // Construct proxy URL
      const serverEndpoint = signedUrlService.serverEndpoint || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net';
      const proxyViewingUrl = `${serverEndpoint}/api/viewer-proxy/${documentId}?token=${encodeURIComponent(idToken)}`;
      
      setProxyUrl(proxyViewingUrl);
      console.log('✅ Proxy URL for Office viewing generated successfully');
      
    } catch (error) {
      console.error('❌ Error generating proxy URL for viewing:', error);
      setProxyUrl(null);
      throw new Error(`Failed to generate proxy URL: ${error.message}`);
    } finally {
      setSignedUrlLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return <FileOutlined />;
    
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return <FilePdfOutlined />;
    if (type.includes('word') || type.includes('doc')) return <FileWordOutlined />;
    if (type.includes('excel') || type.includes('sheet') || type.includes('xls')) return <FileExcelOutlined />;
    if (type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) return <FilePptOutlined />;
    return <FileOutlined />;
  };

  const getFileTypeDisplay = (fileType) => {
    if (!fileType) return 'Unknown';
    
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return 'PDF Document';
    if (type.includes('word') || type.includes('doc')) return 'Word Document';
    if (type.includes('excel') || type.includes('sheet') || type.includes('xls')) return 'Excel Spreadsheet';
    if (type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) return 'PowerPoint Presentation';
    return fileType;
  };

  const canPreviewInBrowser = (fileType) => {
    if (!fileType) return false;
    const type = fileType.toLowerCase();
    return type.includes('pdf');
  };

  const getOfficeViewerUrl = (fileUrl, fileType) => {
    if (!fileType || !fileUrl) return null;
    
    const type = fileType.toLowerCase();
    if (type.includes('word') || type.includes('doc') || 
        type.includes('excel') || type.includes('sheet') || type.includes('xls') ||
        type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) {
      
      console.log('🔗 Using Office Online viewer with proxy URL:', fileUrl);
      
      // Use proxy URL for Office Online (more reliable than signed URLs)
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
      
      return officeViewerUrl;
    }
    return null;
  };

  const getGoogleDocsViewerUrl = (fileUrl, fileType) => {
    if (!fileType || !fileUrl) return null;
    
    const type = fileType.toLowerCase();
    if (type.includes('word') || type.includes('doc') || 
        type.includes('excel') || type.includes('sheet') || type.includes('xls') ||
        type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) {
      
      console.log('📄 Using Google Docs viewer with proxy URL:', fileUrl);
      return `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;
    }
    return null;
  };

  const isOfficeFile = (fileType) => {
    if (!fileType) return false;
    const type = fileType.toLowerCase();
    return type.includes('word') || type.includes('doc') || 
           type.includes('excel') || type.includes('sheet') || type.includes('xls') ||
           type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt');
  };

  const handleDownloadDocument = async () => {
    try {
      console.log('🔐 Initiating download with signed URL');
      
      const downloadUrl = signedUrl || documentData.url;
      
      // Standard download for all file types
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = documentData.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ Download initiated successfully');
      
    } catch (error) {
      console.error('❌ Error downloading document:', error);
      // Fallback to direct URL
      window.open(documentData.url, '_blank');
    }
  };

  /**
   * Enhanced edit workflow for Office documents
   */
  const handleEditDocument = async () => {
    try {
      if (!documentData || !currentUser) {
        message.error('Document or user information not available');
        return;
      }

      console.log('📝 Starting enhanced edit workflow for:', documentData.name);

      // Create edit session
      const sessionId = await createEditSession(
        documentData.id,
        currentUser.uid,
        {
          name: currentUser.displayName || `${currentUser.firstName} ${currentUser.lastName}`,
          email: currentUser.email,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName
        }
      );

      setEditSession(sessionId);

      // Generate smart download info
      const smartDownloadInfo = generateSmartDownloadUrl(documentData, sessionId);
      setDownloadInfo(smartDownloadInfo);

      // Show edit workflow modal
      setCurrentEditStep(0);
      setEditModalVisible(true);

      message.success('Edit session started - follow the steps to edit your document');

    } catch (error) {
      console.error('❌ Error starting edit workflow:', error);
      message.error('Failed to start edit session. Please try again.');
    }
  };

  /**
   * Execute smart download with session tracking
   */
  const executeSmartDownload = async () => {
    try {
      if (!downloadInfo || !editSession) {
        message.error('Download information not available');
        return;
      }

      console.log('⬇️ Executing smart download with session tracking');

      // Update edit session to mark download
      await updateEditSession(editSession, {
        downloadedAt: new Date(),
        status: 'active'
      });

      // Trigger download
      const downloadUrl = signedUrl || documentData.url;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = documentData.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Move to next step
      setCurrentEditStep(1);

      // Set up reminder timer (optional)
      setTimeout(() => {
        if (editSession && currentEditStep === 1) {
          message.info('Remember to upload your changes when you\'re done editing!');
        }
      }, downloadInfo.reminderTime);

      console.log('✅ Smart download completed');

    } catch (error) {
      console.error('❌ Error during smart download:', error);
      message.error('Download failed. Please try again.');
    }
  };

  /**
   * Complete edit session
   */
  const handleCompleteEdit = async () => {
    try {
      if (editSession) {
        await completeEditSession(editSession, 'completed');
        setEditSession(null);
      }
      setEditModalVisible(false);
      setCurrentEditStep(0);
      setDownloadInfo(null);
      
      message.success('Edit session completed');
    } catch (error) {
      console.error('❌ Error completing edit session:', error);
    }
  };

  /**
   * Cancel edit session
   */
  const handleCancelEdit = async () => {
    try {
      if (editSession) {
        await completeEditSession(editSession, 'cancelled');
        setEditSession(null);
      }
      setEditModalVisible(false);
      setCurrentEditStep(0);
      setDownloadInfo(null);
    } catch (error) {
      console.error('❌ Error cancelling edit session:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="p-4">
        <PageHeader title="Document Viewer" />
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading document...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <PageHeader title="Document Viewer" />
        <div className="alert alert-danger" role="alert">
          <h5>Error Loading Document</h5>
          <p>{error}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => navigate('/documents')}
            style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
          >
            Back to Documents
          </button>
        </div>
      </div>
    );
  }

  if (!documentData) {
    return (
      <div className="p-4">
        <PageHeader title="Document Viewer" />
        <div className="alert alert-warning" role="alert">
          Document not found
        </div>
      </div>
    );
  }

  // Use appropriate URL based on file type with robust fallback
  const getEffectiveViewingUrl = () => {
    if (isOfficeFile(documentData?.type)) {
      // For Office files, prefer proxy URL, fallback to signed URL, then original URL
      return proxyUrl || signedUrl || documentData?.url;
    } else {
      // For non-Office files (PDFs), use signed URL with fallback to original
      return signedUrl || documentData?.url;
    }
  };

  const viewingUrl = getEffectiveViewingUrl();
  const isPdfPreviewable = canPreviewInBrowser(documentData?.type);
  
  // For Office files, try both proxy and signed URLs for viewers
  const getOfficeViewerUrls = () => {
    if (!isOfficeFile(documentData?.type)) return { office: null, google: null };
    
    const urlToUse = proxyUrl || signedUrl || documentData?.url;
    return {
      office: urlToUse ? getOfficeViewerUrl(urlToUse, documentData.type) : null,
      google: urlToUse ? getGoogleDocsViewerUrl(urlToUse, documentData.type) : null
    };
  };

  const { office: officeViewerUrl, google: googleDocsUrl } = getOfficeViewerUrls();
  const canPreview = isPdfPreviewable || officeViewerUrl;

  return (
    <div className="p-0" style={{ height: 'calc(100vh - 64px)' }}>
      <PageHeader title={document.name} />

      <div className="row g-0" style={{ height: 'calc(100% - 64px)' }}>
        <div className="col-12 h-100">
          <div className="card shadow-sm h-100">
            {/* Document Info Header */}
            <div className="card-header bg-light">
              <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex align-items-center">
                  <span className="me-2" style={{ fontSize: '1.2em' }}>
                    {getFileIcon(document.type)}
                  </span>
                  <div>
                    <h6 className="mb-0">{document.name}</h6>
                    <small className="text-muted">
                      {getFileTypeDisplay(document.type)} • {formatFileSize(document.size)} • 
                      Version {document.version} • 
                      Uploaded by {document.uploadedBy}
                      {signedUrlLoading && <span className="text-info ms-2">• Preparing secure access...</span>}
                    </small>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  {isOfficeFile(document.type) && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={handleEditDocument}
                      disabled={signedUrlLoading}
                      style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
                    >
                      Edit in Office
                    </Button>
                  )}
                  <button
                    onClick={handleDownloadDocument}
                    className="btn btn-sm d-flex align-items-center btn-outline-primary"
                    disabled={signedUrlLoading}
                  >
                    <DownloadOutlined className="me-1" />
                    {signedUrlLoading ? 'Preparing...' : 'Download'}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigate('/documents')}
                  >
                    Back to Documents
                  </button>
                </div>
              </div>
              
              {/* Collaboration Status */}
              <div style={{ marginTop: '12px', borderTop: '1px solid #e6f4ff', paddingTop: '12px' }}>
                <DocumentCollaborationStatus 
                  documentId={document.id}
                  compact={false}
                  showVersionInfo={true}
                  onEditClick={isOfficeFile(document.type) ? handleEditDocument : null}
                />
              </div>
            </div>

            {/* Document Preview */}
            <div className="card-body p-0 h-100">
              {signedUrlLoading ? (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-4">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Preparing secure document access...</span>
                  </div>
                  <h5 className="mb-2">Preparing Secure Access</h5>
                  <p className="text-muted">Configuring secure document viewing for maximum compatibility...</p>
                </div>
              ) : canPreview ? (
                <div className="document-preview h-100" style={{ backgroundColor: 'white', position: 'relative' }}>
                  {/* Viewer Selection for Office Files */}
                  {isOfficeFile(document.type) && (
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      zIndex: 1000,
                      background: 'rgba(255,255,255,0.95)',
                      padding: '8px',
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}>
                      <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>Viewer Options:</div>
                      <button
                        onClick={() => setCurrentViewer('office')}
                        style={{
                          background: currentViewer === 'office' ? '#39325b' : '#f0f0f0',
                          color: currentViewer === 'office' ? 'white' : '#333',
                          border: 'none',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          marginRight: '4px'
                        }}
                      >
                        Office Online
                      </button>
                      <button
                        onClick={() => setCurrentViewer('google')}
                        style={{
                          background: currentViewer === 'google' ? '#39325b' : '#f0f0f0',
                          color: currentViewer === 'google' ? 'white' : '#333',
                          border: 'none',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        Google Docs
                      </button>
                    </div>
                  )}
                  
                  <iframe
                    src={isPdfPreviewable ? 
                      `${viewingUrl}#toolbar=0&navpanes=0` : 
                      (currentViewer === 'google' && googleDocsUrl) ? googleDocsUrl : officeViewerUrl
                    }
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      border: 'none',
                      backgroundColor: 'white',
                      display: 'block'
                    }}
                    title={`${document.name} Preview`}
                    onLoad={(e) => {
                      console.log('📄 Iframe loaded successfully');
                      setViewerError(false);
                    }}
                    onError={(e) => {
                      console.error('❌ Iframe failed to load:', e);
                      setViewerError(true);
                      if (currentViewer === 'office') {
                        console.log('🔄 Switching to Google Docs viewer');
                        setCurrentViewer('google');
                      }
                    }}
                  />
                  
                  {/* Enhanced error overlay with better diagnostics */}
                  {viewerError && isOfficeFile(document.type) && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'white',
                      padding: '20px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      textAlign: 'center',
                      maxWidth: '500px'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '12px' }}>⚠️</div>
                      <h6>Viewer Loading Issue</h6>
                      <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                        The {currentViewer === 'office' ? 'Office Online' : 'Google Docs'} viewer is having trouble loading this file.
                      </p>
                      <p style={{ fontSize: '12px', color: '#888', marginBottom: '16px' }}>
                        Using {proxyUrl ? 'secure proxy' : signedUrl ? 'signed URL' : 'direct'} access method.
                        {!proxyUrl && signedUrl && ' (Proxy unavailable, using fallback)'}
                      </p>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            setViewerError(false);
                            setCurrentViewer('google');
                          }}
                          style={{
                            background: '#4285f4',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Try Google Viewer
                        </button>
                        <button
                          onClick={handleDownloadDocument}
                          style={{
                            background: '#52c41a',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Download Instead
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-4">
                  <div style={{ fontSize: '4em', color: '#1976d2', marginBottom: '1rem' }}>
                    {getFileIcon(document.type)}
                  </div>
                  <h5 className="mb-3">{document.name}</h5>
                  <p className="text-muted mb-4">
                    {isOfficeFile(document.type) ? 
                      'This Office document will open directly in your desktop application.' :
                      'This file type cannot be previewed in the browser.'
                    }
                    <br />
                    <strong>Click the button above</strong> to open in {document.type?.toLowerCase().includes('excel') ? 'Microsoft Excel' : 
                                                                 document.type?.toLowerCase().includes('word') ? 'Microsoft Word' : 
                                                                 document.type?.toLowerCase().includes('powerpoint') ? 'Microsoft PowerPoint' : 
                                                                 'the appropriate application'}.
                  </p>
                  
                  {isOfficeFile(document.type) && (
                    <div className="alert alert-success" style={{ maxWidth: '600px', marginBottom: '20px' }}>
                      <h6 className="alert-heading">📊 Office Document Ready</h6>
                      <p className="mb-2">
                        This {getFileTypeDisplay(document.type)} is securely stored and will open directly in your installed Office application.
                      </p>
                      <p className="mb-0">
                        <strong>💡 How it works:</strong> When you click the button, your browser will open the file 
                        in {document.type?.toLowerCase().includes('excel') ? 'Excel' : 
                            document.type?.toLowerCase().includes('word') ? 'Word' : 
                            document.type?.toLowerCase().includes('powerpoint') ? 'PowerPoint' : 
                            'your Office app'} where you can view, edit, and save changes.
                      </p>
                    </div>
                  )}
                  
                  {!isOfficeFile(document.type) && !canPreviewInBrowser(document.type) && (
                    <div className="alert alert-info" style={{ maxWidth: '600px', marginBottom: '20px' }}>
                      <h6 className="alert-heading">📄 File Download</h6>
                      <p className="mb-0">
                        This file type requires download to view. Click the download button above to save 
                        the file to your device and open it with the appropriate application.
                      </p>
                    </div>
                  )}
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-title">File Information</h6>
                      <ul className="list-unstyled mb-0">
                        <li><strong>Type:</strong> {getFileTypeDisplay(document.type)}</li>
                        <li><strong>Size:</strong> {formatFileSize(document.size)}</li>
                        <li><strong>Version:</strong> {document.version}</li>
                        <li><strong>Uploaded:</strong> {document.createdAt?.toDate ? 
                          document.createdAt.toDate().toLocaleString() : 
                          new Date(document.createdAt).toLocaleString()}
                        </li>
                        <li><strong>Uploaded by:</strong> {document.uploadedBy}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Edit Workflow Modal */}
      <Modal
        title="Edit Document in Desktop Office"
        open={editModalVisible}
        onCancel={handleCancelEdit}
        width={600}
        footer={null}
        centered
      >
        <div style={{ padding: '20px 0' }}>
          <Steps
            current={currentEditStep}
            size="small"
            style={{ marginBottom: '24px' }}
            items={[
              {
                title: 'Download',
                description: 'Download file for editing'
              },
              {
                title: 'Edit',
                description: 'Edit in your Office app'
              },
              {
                title: 'Upload',
                description: 'Upload your changes'
              }
            ]}
          />

          {currentEditStep === 0 && downloadInfo && (
            <div>
              <Alert
                message="Ready to Edit"
                description="Your edit session has been created. Click the button below to download the file and open it in your desktop Office application."
                type="success"
                showIcon
                style={{ marginBottom: '20px' }}
              />
              
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0f8ff', 
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h6 style={{ marginBottom: '12px', color: '#1890ff' }}>
                  📋 How to Edit:
                </h6>
                <ol style={{ marginBottom: '0', paddingLeft: '20px' }}>
                  <li>{downloadInfo.instructions.step1}</li>
                  <li>{downloadInfo.instructions.step2}</li>
                  <li>{downloadInfo.instructions.step3}</li>
                  <li>{downloadInfo.instructions.step4}</li>
                </ol>
              </div>

              <div style={{ textAlign: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<DownloadOutlined />}
                  onClick={executeSmartDownload}
                  style={{ 
                    backgroundColor: '#39325b', 
                    borderColor: '#39325b',
                    minWidth: '200px'
                  }}
                >
                  Download for Editing
                </Button>
              </div>
            </div>
          )}

          {currentEditStep === 1 && (
            <div>
              <Alert
                message="File Downloaded Successfully"
                description="The file should now be open in your desktop Office application. Make your changes and save the file, then return here to upload the new version."
                type="info"
                showIcon
                style={{ marginBottom: '20px' }}
              />
              
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f6ffed', 
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h6 style={{ marginBottom: '12px', color: '#52c41a' }}>
                  ✅ Currently Editing:
                </h6>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Document:</strong> {downloadInfo.documentName}
                </p>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Started:</strong> {new Date(downloadInfo.downloadedAt).toLocaleTimeString()}
                </p>
                <p style={{ marginBottom: '0', fontSize: '12px', color: '#666' }}>
                  Remember to save your changes in the Office application before uploading.
                </p>
              </div>

              <div style={{ textAlign: 'center' }}>
                <Space>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                      setCurrentEditStep(2);
                      message.info('Ready to upload your changes!');
                    }}
                    style={{ 
                      backgroundColor: '#52c41a', 
                      borderColor: '#52c41a'
                    }}
                  >
                    I'm Done Editing
                  </Button>
                  <Button
                    size="large"
                    onClick={handleCancelEdit}
                  >
                    Cancel Edit Session
                  </Button>
                </Space>
              </div>
            </div>
          )}

          {currentEditStep === 2 && (
            <div>
              <Alert
                message="Upload Your Changes"
                description="Navigate to the Documents page to upload the new version of your file, or use the upload functionality in the current document view."
                type="warning"
                showIcon
                style={{ marginBottom: '20px' }}
              />
              
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fff7e6', 
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h6 style={{ marginBottom: '12px', color: '#fa8c16' }}>
                  📤 Next Steps:
                </h6>
                <ul style={{ marginBottom: '0', paddingLeft: '20px' }}>
                  <li>Go to the Documents page</li>
                  <li>Find this document in the list</li>
                  <li>Click "Upload New Version"</li>
                  <li>Select your edited file</li>
                  <li>Add a description of your changes</li>
                </ul>
              </div>

              <div style={{ textAlign: 'center' }}>
                <Space>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                      handleCompleteEdit();
                      navigate('/documents');
                    }}
                    style={{ 
                      backgroundColor: '#39325b', 
                      borderColor: '#39325b'
                    }}
                  >
                    Go to Documents
                  </Button>
                  <Button
                    size="large"
                    onClick={handleCompleteEdit}
                  >
                    Complete Session
                  </Button>
                </Space>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DocumentViewer; 